import { css, cx } from "@emotion/css";
import { ChevronDownIcon, ExclamationIcon, XmarkIcon } from "@octopusdeploy/design-system-icons";
import { space, themeTokens, borderWidth, text, borderRadius } from "@octopusdeploy/design-system-tokens";
import debounce from "lodash.debounce";
import { useRef, useId, useState, useMemo } from "react";
import type { ReactElement, ReactNode } from "react";
import type React from "react";
import { useObserveElementSize } from "../../../hooks/useObserveElementSize";
import { accessibilityStyles, resetStyles } from "../../../utils";
import { alphanumericSort } from "../../../utils/alphanumericSort";
import { Button } from "../../Button/Button";
import { useDropdownButton } from "../../Dropdown/useDropdownButton";
import { SearchInput } from "../../Input/SearchInput";
import type { PopoverBasicHelpProps } from "../../Popover/PopoverBasicHelp";
import { TagGroup } from "../../Tag/TagGroup";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import InputLabel from "../Primitives/InputLabel";
import type { ListboxElement } from "../Primitives/Listbox";
import { Listbox } from "../Primitives/Listbox";
import { inputDisabledStyles, inputErrorStyles, inputReadOnlyStyles, inputStyles } from "../Primitives/SharedFormStyling.styles";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
import { remToPx } from "../utils/remToPx";
import type { AsyncList } from "./AsyncList";
import { MultiSelectCheckbox } from "./MultiSelectCheckbox";
import { SelectDropdown } from "./SelectDropdown";
import type { SelectOption } from "./SelectOption";
import { selectOptionHeight, selectOptionWithDescriptionHeight } from "./SelectOptionLayout";
export interface SharedSelectProps<T> {
    /**
     * The items for the select
     */
    items: T[] | AsyncList<T>;
    /**
     * Controls whether items are sorted before displaying in the list.
     * Does not have any effect on AsyncList items.
     * @default false
     */
    sortItems?: boolean;
    /**
     * A function that returns a SelectOption for an item
     */
    getOption: (item: T) => SelectOption;
    /**
     * The label for the select
     */
    label: string;
    /**
     * The placeholder text to display when input is empty
     */
    placeholder?: string;
    /**
     * The description text to display below the input
     */
    description?: string | DescriptionContent;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether to show optional marker
     */
    hasOptionalMarker?: boolean;
    /**
     * Whether this field has a default value marker
     */
    hasDefaultMarker?: boolean;
    /**
     * PopoverBasicHelp component to display additional help information
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * Whether to autofocus the input
     */
    autoFocus?: boolean;
    /**
     * Whether the input is readonly
     */
    readOnly?: boolean;
    /**
     * Whether to allow filtering the options.
     */
    allowFilter?: boolean;
    /**
     * Whether to allow clearing the selection.
     */
    allowClear?: boolean;
}
export interface SelectBaseProps<T> extends SharedSelectProps<T> {
    value: string | string[] | undefined;
    isMultiSelectable: boolean;
    renderOption: (option: SelectOption, selected: boolean) => ReactElement;
    onOptionsSelected: (options: SelectedOption[]) => void;
    renderSelection: (options: SelectedOption[]) => ReactNode;
    clearSelection: () => void;
}
export function SelectBase<T>({ items: itemsArrayOrAsyncList, sortItems = false, getOption, label, value, placeholder, description, error, disabled, hasRequiredMarker, hasOptionalMarker, hasDefaultMarker, popover, autoFocus, readOnly, onOptionsSelected, allowFilter = false, allowClear = false, isMultiSelectable, renderSelection, renderOption, clearSelection, }: SelectBaseProps<T>) {
    const dropdownButtonState = useDropdownButton({ trapFocus: true, dropdownAriaRole: "listbox" });
    const { isOpen, closeDropdown, buttonElement: selectElement, setButtonRef: setSelectRef, toggleDropdown, setDropdownRef, dropdownAttributes, buttonAriaAttributes } = dropdownButtonState;
    const [suffixElement, setSuffixElement] = useState<HTMLDivElement | null>(null);
    const selectContainerRef = useRef<HTMLDivElement | null>(null);
    const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
    const baseId = useId();
    const labelHtmlId = `label-${baseId}`;
    const valueHtmlId = `value-${baseId}`;
    const selectHtmlId = `select-${baseId}`;
    const multiSelectSummaryHtmlId = `multiselect-summary-${baseId}`;
    const listboxHtmlId = `listbox-${baseId}`;
    const listboxRef = useRef<ListboxElement | null>(null);
    const getOptionHtmlId = (index: number) => `${dropdownAttributes.dropdownId}-option-${index}`;
    const [activeDescendantId, setActiveDescendantId] = useState<string | undefined>(undefined);
    const [inputValue, setInputValue] = useState("");
    const [filterText, setFilterText] = useState("");
    const isAsync = !Array.isArray(itemsArrayOrAsyncList);
    const isLoading = isAsync && itemsArrayOrAsyncList.isLoading;
    const hasAsyncError = isAsync && itemsArrayOrAsyncList.error !== null;
    const debouncedSetFilterText = useMemo(() => debounce((value: string) => {
        setFilterText(value);
        if (isAsync)
            itemsArrayOrAsyncList.setFilterText(value);
    }, 200), [isAsync, itemsArrayOrAsyncList]);
    const currentItems = isAsync ? itemsArrayOrAsyncList.loadedItems : itemsArrayOrAsyncList;
    const currentOptions = useMemo(() => currentItems.map(getOption), [currentItems, getOption]);
    const optionsToShow = useMemo(() => {
        // Any filtering and sorting for async lists is handled externally so is accounted for in currentOptions
        if (isAsync) {
            return currentOptions;
        }
        const filteredOptions = filterText ? currentOptions.filter((option) => option.label.toLowerCase().includes(filterText.trim().toLowerCase())) : currentOptions;
        return sortItems ? alphanumericSort(filteredOptions, (option) => option.label.toLowerCase()) : filteredOptions;
    }, [isAsync, filterText, currentOptions, sortItems]);
    const valuesSet = useMemo(() => {
        if (!value)
            return new Set<string>();
        return new Set(Array.isArray(value) ? value : [value]);
    }, [value]);
    const selectedOptions: SelectedOption[] = useMemo(() => {
        if (isAsync) {
            return Array.from(valuesSet).map((v) => {
                const item = itemsArrayOrAsyncList.getItemById(v);
                return item ? getOption(item) : ({ missing: true, value: v } as const);
            });
        }
        const currentOptionsMap = new Map(currentOptions.map((option) => [option.value, option]));
        return Array.from(valuesSet).map((v) => currentOptionsMap.get(v) ?? ({ missing: true, value: v } as const));
    }, [currentOptions, getOption, isAsync, itemsArrayOrAsyncList, valuesSet]);
    useObserveElementSize(suffixElement, ({ width }) => {
        selectContainerRef.current?.style.setProperty(suffixWidthCssVar, `${width}px`);
    }, () => {
        selectContainerRef.current?.style.removeProperty(suffixWidthCssVar);
    });
    const handleOptionActive = (index: number) => {
        setActiveDescendantId(getOptionHtmlId(index));
    };
    const handleOptionSelected = (option: SelectedOption) => {
        onOptionsSelected([option]);
        if (!isMultiSelectable) {
            closeDropdown({ moveFocusToButton: true });
        }
    };
    const handleSelectKeyDown = (event: React.KeyboardEvent) => {
        const { key } = event;
        if (key === "ArrowDown" || key === "Enter" || key === " ") {
            event.preventDefault();
            toggleDropdown();
        }
    };
    const handleLoadMore = isAsync && !itemsArrayOrAsyncList.isLoading ? itemsArrayOrAsyncList.loadMore : undefined;
    const handleSearchKeyDown = (event: React.KeyboardEvent) => {
        const { key } = event;
        if (searchInputKeysToPassToListbox.has(key)) {
            listboxRef.current?.handleKeyDown(event);
        }
    };
    const handleFilterTextChange = (value: string) => {
        setInputValue(value);
        debouncedSetFilterText(value);
    };
    if (!isOpen && (inputValue || filterText)) {
        setInputValue("");
        setFilterText("");
        debouncedSetFilterText.cancel();
        if (isAsync) {
            itemsArrayOrAsyncList.setFilterText("");
        }
    }
    const showClearAllButton = allowClear && (isMultiSelectable ? valuesSet.size > 1 : valuesSet.size > 0);
    const hasSomeOptionsSelected = optionsToShow.some((option) => valuesSet.has(option.value));
    const hasAllOptionsSelected = optionsToShow.every((option) => valuesSet.has(option.value));
    const selectAll = () => onOptionsSelected(optionsToShow);
    const deselectAll = () => onOptionsSelected(optionsToShow.filter((option) => valuesSet.has(option.value)));
    const visuallyHiddenSummary = selectedOptions.length > 1 ? `${selectedOptions.length} options selected: ${selectedOptions.map(getSelectedOptionLabel).join(", ")}` : selectedOptions.length === 1 ? getSelectedOptionLabel(selectedOptions[0]) : undefined;
    const canChange = !disabled && !readOnly;
    return (<div className={layoutStyles}>
            <div>
                <InputLabel id={labelHtmlId} label={label} htmlFor={selectHtmlId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${selectHtmlId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <div ref={selectContainerRef} className={selectStyles.container}>
                <button id={selectHtmlId} ref={setSelectRef} role="combobox" onClick={canChange ? toggleDropdown : undefined} onKeyDown={handleSelectKeyDown} aria-activedescendant={isOpen ? activeDescendantId : undefined} aria-readonly={readOnly} aria-required={hasRequiredMarker} tabIndex={0} autoFocus={autoFocus} className={cx(selectStyles.buttonReset, inputStyles, selectStyles.select, {
            [inputErrorStyles]: !!error,
            [inputDisabledStyles]: disabled,
            [inputReadOnlyStyles]: readOnly,
            [selectStyles.readOnly]: readOnly,
        })} aria-disabled={disabled} aria-invalid={!!error} aria-describedby={[description ? `${selectHtmlId}-description` : null, error ? `${selectHtmlId}-error` : null].filter(Boolean).join(" ") || undefined} {...buttonAriaAttributes}>
                    <span className={selectStyles.visuallyHiddenSummary}>{visuallyHiddenSummary}</span>
                    {selectedOptions.length === 0 && <span className={cx(selectStyles.value, selectStyles.placeholder)}>{placeholder}</span>}
                </button>
                <div id={valueHtmlId} className={selectStyles.valueContainer}>
                    {selectedOptions.length > 0 && <span className={selectStyles.value}>{renderSelection(selectedOptions)} </span>}
                </div>

                <div ref={setSuffixElement} className={selectStyles.suffix}>
                    {showClearAllButton && canChange ? (<span className={cx(selectStyles.clearButton)}>
                            <Button importance="ghost" icon={<XmarkIcon size={20}/>} size="small" onClick={clearSelection} accessibleName="Clear selection"/>
                        </span>) : undefined}
                    <span className={cx(selectStyles.chevronIcon, { [selectStyles.rotatedChevronIcon]: isOpen })}>
                        <ChevronDownIcon size={16}/>
                    </span>
                </div>

                {isOpen && (<SelectDropdown ref={setDropdownRef} dropdownButtonState={dropdownButtonState}>
                        {(allowFilter || isMultiSelectable) && (<div className={selectStyles.dropdownHeader}>
                                {allowFilter && (<SearchInput value={inputValue} onChange={handleFilterTextChange} fullWidth={true} autoFocus={true} accessibleName={`Filter ${label}`} onKeyDown={handleSearchKeyDown} onFocus={() => setIsSearchInputFocused(true)} onBlur={() => setIsSearchInputFocused(false)} aria-activedescendant={activeDescendantId} aria-controls={listboxHtmlId} aria-autocomplete="list"/>)}
                                {isMultiSelectable && (<button type="button" className={selectStyles.selectDeselectAll} disabled={optionsToShow.length === 0} onClick={hasSomeOptionsSelected ? deselectAll : selectAll} tabIndex={isOpen ? 0 : -1}>
                                        <MultiSelectCheckbox isDisabled={optionsToShow.length === 0} checked={hasSomeOptionsSelected} isIndeterminate={hasSomeOptionsSelected && !hasAllOptionsSelected}/>
                                        {hasSomeOptionsSelected ? "Deselect all" : "Select all"}
                                    </button>)}
                            </div>)}
                        {optionsToShow.length === 0 && !isLoading && !hasAsyncError ? (<div className={selectStyles.noResults}>No results found.</div>) : (<Listbox id={listboxHtmlId} ref={listboxRef} options={optionsToShow} value={value} renderOption={renderOption} getOptionHeight={(option) => (option.description ? selectOptionWithDescriptionHeight : selectOptionHeight)} onOptionSelected={handleOptionSelected} getOptionHtmlId={getOptionHtmlId} accessibleName={label} onOptionActive={handleOptionActive} onLoadMore={handleLoadMore} isMultiSelectable={isMultiSelectable} focusType={allowFilter ? (isSearchInputFocused ? "sub-focus" : "none") : "focus"} scrollPaddingTop={allowFilter || isMultiSelectable ? remToPx(space[16]) : undefined} scrollPaddingBottom={allowFilter || isMultiSelectable ? remToPx(space[12]) : undefined} isLoading={isLoading} hasError={hasAsyncError}/>)}
                    </SelectDropdown>)}
            </div>
            {isMultiSelectable && valuesSet.size > 1 && (<div id={multiSelectSummaryHtmlId}>
                    <TagGroup tags={selectedOptions.map((option) => ({
                label: getSelectedOptionLabel(option),
                prefix: isMissingSelectedOption(option) ? <ExclamationIcon size={16}/> : undefined,
                onDismiss: canChange ? () => handleOptionSelected(option) : undefined,
            }))}/>
                </div>)}
            {error && <InputError error={error} id={`${selectHtmlId}-error`}/>}
        </div>);
}
export interface MissingSelectedOption {
    missing: true;
    value: string;
}
export type SelectedOption = SelectOption | MissingSelectedOption;
export function isMissingSelectedOption(option: SelectedOption): option is MissingSelectedOption {
    return "missing" in option;
}
export function getSelectedOptionLabel(option: SelectedOption) {
    return isMissingSelectedOption(option) ? "Missing" : option.label;
}
const searchInputKeysToPassToListbox = new Set(["ArrowUp", "ArrowDown", "Home", "End", "Enter"]);
const layoutStyles = css({
    display: "grid",
    position: "relative",
    gap: space[8],
    width: "100%",
    maxWidth: "600px",
});
const suffixWidthCssVar = `--select-suffix-width`;
const selectStyles = {
    container: css({
        position: "relative",
        minWidth: 0,
    }),
    buttonReset: css(resetStyles.button),
    select: css({
        display: "flex",
        alignItems: "center",
        gap: space[6],
        height: "2.625rem",
        padding: `0 var(${suffixWidthCssVar}, ${space[8]}) 0 ${space[8]}`,
    }),
    valueContainer: css({
        display: "flex",
        alignItems: "center",
        position: "absolute",
        inset: 0,
        padding: `0 var(${suffixWidthCssVar}, ${space[8]}) 0 ${space[8]}`,
        pointerEvents: "none",
    }),
    value: css({
        display: "inline-block",
        font: text.body.regular.large,
        color: themeTokens.color.text.primary,
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        overflow: "hidden",
        // The value is absolutely positioned over the select button with pointerEvents: none.
        // Re-enable it for any interactable elements in the value
        pointerEvents: "auto",
    }),
    placeholder: css({
        color: themeTokens.color.text.tertiary,
    }),
    readOnly: css({
        cursor: "unset",
    }),
    suffix: css({
        display: "flex",
        alignItems: "center",
        position: "absolute",
        padding: `0 ${space[8]}`,
        right: 0,
        top: 0,
        bottom: 0,
        pointerEvents: "none",
    }),
    clearButton: css({
        pointerEvents: "auto",
    }),
    chevronIcon: css({
        transition: "transform 150ms ease-in-out",
        padding: space[2],
    }),
    rotatedChevronIcon: css({
        transform: "rotate(-180deg)",
    }),
    dropdownHeader: css({
        display: "flex",
        flexDirection: "column",
        backgroundColor: themeTokens.color.background.primary.default,
        padding: space[12],
        gap: space[12],
        borderBottom: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
    }),
    noResults: css({
        padding: space[16],
        color: themeTokens.color.text.secondary,
        font: text.body.regular.large,
        margin: "auto",
    }),
    selectDeselectAll: css({
        ...resetStyles.button,
        display: "flex",
        alignItems: "center",
        gap: space[8],
        boxSizing: "border-box",
        padding: `${space[6]} ${space[6]}`,
        borderRadius: borderRadius.small,
        font: text.body.regular.large,
        color: themeTokens.color.text.primary,
        "&:hover, &:focus": {
            backgroundColor: themeTokens.color.menuList.background.hover,
        },
        "&:active": {
            backgroundColor: themeTokens.color.menuList.background.active,
        },
        "&:disabled": {
            color: themeTokens.color.text.tertiary,
            pointerEvents: "none",
        },
    }),
    visuallyHiddenSummary: css({
        ...accessibilityStyles.hidden,
    }),
};
